import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {LayoutPortal} from '../../LayoutPortal';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {usePortalClientId} from '../../hooks/usePortalClientId';
import {ClientMemoryApi} from '../../api/clientMemoryApi';
import {FACT_TYPE_ORDER, factTypeLabelKey} from '../client/memory/factConstants';

// Vista de Memoria del PORTAL (lo que ve el cliente final): read-only, solo lo que la agencia
// compartió. La data viene client-safe del backend (sin provenance/crudo). Agrupa los hechos
// por tipo y lista las notas/resúmenes compartidos.
export const PortalMemory = () => {
    const {t} = useTranslation();
    const clientId = usePortalClientId();
    const [data, setData] = useState(null);

    useEffect(() => {
        ClientMemoryApi.portalMemory(clientId)
            .then((resp) => {
                if (resp && Array.isArray(resp.facts) && Array.isArray(resp.memories)) {
                    setData({facts: resp.facts, memories: resp.memories});
                } else {
                    setData({facts: [], memories: []});
                    toast.error(t('portal_memory.load_error'));
                }
            })
            .catch(() => {
                setData({facts: [], memories: []});
                toast.error(t('portal_memory.load_error'));
            });
    }, [clientId, t]);

    const renderFacts = (facts) => {
        const grouped = FACT_TYPE_ORDER
            .map((type) => ({type, items: facts.filter((f) => f.fact_type === type)}))
            .filter((g) => g.items.length > 0);
        if (grouped.length === 0) return null;
        return (
            <section className="portal-memory__section">
                <h2 className="portal-memory__section-title">{t('portal_memory.facts_title')}</h2>
                {grouped.map((group) => (
                    <div key={group.type} className="portal-memory__group">
                        <h3 className="portal-memory__group-title">{t(factTypeLabelKey(group.type))}</h3>
                        <ul className="portal-memory__facts">
                            {group.items.map((fact) => (
                                <li key={fact.id} className="portal-memory__fact">{fact.statement}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </section>
        );
    };

    const renderNotes = (memories) => {
        if (memories.length === 0) return null;
        return (
            <section className="portal-memory__section">
                <h2 className="portal-memory__section-title">{t('portal_memory.notes_title')}</h2>
                <div className="portal-memory__notes">
                    {memories.map((memory) => (
                        <div key={memory.id} className="portal-memory__note">
                            <p className="portal-memory__note-text">{memory.text}</p>
                            {memory.business_unit_name && (
                                <span className="portal-memory__note-bu">{memory.business_unit_name}</span>
                            )}
                        </div>
                    ))}
                </div>
            </section>
        );
    };

    const renderContent = () => {
        if (data.facts.length === 0 && data.memories.length === 0) {
            return (
                <div className="portal-memory__empty">
                    <FaIcon iconName="brain"/>
                    <p>{t('portal_memory.empty')}</p>
                </div>
            );
        }
        return (
            <>
                {renderFacts(data.facts)}
                {renderNotes(data.memories)}
            </>
        );
    };

    return (
        <LayoutPortal>
            <div className="portal-embedded-page portal-memory">
                <header className="portal-memory__head">
                    <h1 className="portal-memory__title">{t('portal_memory.title')}</h1>
                    <p className="portal-memory__subtitle">{t('portal_memory.subtitle')}</p>
                </header>
                {!data ? (
                    <div className="portal-memory__loader"><Spinner color="positive"/></div>
                ) : renderContent()}
            </div>
        </LayoutPortal>
    );
};

export default PortalMemory;
